import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const I18nContext = createContext(null);
const STORAGE_KEY = "ficopilot_lang_v1";
const SUPPORTED = ["en", "de"];

const dictionary = {
  en: {
    // Nav
    "nav.home": "Home",
    "nav.teammates": "Teammates",
    "nav.play": "What to Play",
    "nav.coach": "AI Coach",
    "nav.players": "Players",
    "nav.squads": "Squads",
    "nav.messages": "Messages",
    "nav.trends": "Trends",
    "nav.builds": "Meta Builds",
    "nav.fps": "FPS Fix",
    "nav.premium": "Premium",
    "nav.admin": "Admin",
    "nav.profile": "Profile",
    "nav.features": "Features",
    "nav.pricing": "Pricing",
    "nav.signin": "Sign in",
    "nav.signup": "Get Started",
    "nav.signout": "Sign out",

    // Landing
    "lp.kicker": "Global gaming assistant",
    "lp.headline_1": "The Global",
    "lp.headline_2": "Gaming Assistant",
    "lp.sub": "Find teammates, improve performance, discover trends, and level up worldwide. Built for serious gamers, not chaos.",
    "lp.cta_primary": "Get Started — It's Free",
    "lp.cta_secondary": "Explore Trends",
    "lp.stats.pilots": "Pilots worldwide",
    "lp.stats.countries": "Countries active",
    "lp.stats.games": "Games tracked",
    "lp.stats.uptime": "Uptime 7d",
    "lp.stats.early_label": "Early Access",
    "lp.founding_badge": "Founding Pilot",
    "lp.founding_remaining": "only {n} of 100 spots left",
    "lp.ig.kicker": "Partner Deals",
    "lp.ig.title": "🔥 Deals for pilots",
    "lp.ig.sub": "Games, credits and offers for real gamers.",
    "lp.ig.affiliate": "Affiliate partner of Instant Gaming",
    "teammates.empty_h": "No pilots found yet",
    "teammates.empty_sub": "Be one of the first pilots to build the community.",
    "teammates.empty_cta": "Complete your profile",
    "sub.h": "Manage subscription",
    "sub.active": "Active",
    "sub.cancel_scheduled": "Cancellation scheduled",
    "sub.renews_on": "Next charge on",
    "sub.ends_on": "Premium stays active until",
    "sub.portal_btn": "Manage subscription",
    "sub.cancel_btn": "Cancel Premium",
    "sub.reactivate_btn": "Reactivate Premium",
    "sub.cancel_modal_h": "Cancel Premium?",
    "sub.cancel_modal_sub": "Your Premium access will stay active until the end of the current billing period.",
    "sub.cancel_modal_no": "Cancel",
    "sub.cancel_modal_yes": "Cancel Premium",
    "sub.cancel_ok": "Premium will be canceled at the end of the period.",
    "sub.cancel_failed": "Cancellation failed.",
    "sub.reactivate_modal_h": "Reactivate Premium?",
    "sub.reactivate_modal_sub": "Your Premium subscription will continue normally.",
    "sub.reactivate_ok": "Premium reactivated.",
    "sub.reactivate_failed": "Reactivation failed.",
    "sub.portal_failed": "Could not open the billing portal.",
    "danger.h": "Danger Zone",
    "danger.sub": "Irreversible actions — be sure before clicking.",
    "danger.deactivate_btn": "Deactivate account",
    "danger.deactivate_hint": "Profile + data will be deactivated. Login will be blocked.",
    "danger.deactivate_modal_h": "Deactivate account?",
    "danger.deactivate_modal_sub": "Your profile, teams and data will be deactivated. This action may not be reversible.",
    "danger.deactivate_modal_no": "Cancel",
    "danger.deactivate_modal_yes": "Deactivate account",
    "danger.deactivate_ok": "Account deactivated.",
    "danger.deactivate_failed": "Deactivation failed.",
    "lp.features.kicker": "Core tools",
    "lp.features.title": "Everything a serious pilot needs",
    "lp.features.sub": "No social chaos. Just focused tools that make your next session better.",
    "lp.f1.title": "Find Teammates",
    "lp.f1.desc": "Match with gamers worldwide by game, country, language, and platform.",
    "lp.f2.title": "What Should I Play?",
    "lp.f2.desc": "Personalized picks for your mood, time budget, and squad size.",
    "lp.f3.title": "FPS & Ping Fix",
    "lp.f3.desc": "A wizard that diagnoses lag, stutters and crashes in under 60 seconds.",
    "lp.f4.title": "Country Trends",
    "lp.f4.desc": "See what is popping off in your region — live player counts, peak hours.",
    "lp.f5.title": "Meta Builds",
    "lp.f5.desc": "Tier-ranked loadouts, sensitivity settings, and role-specific strategies.",
    "lp.f6.title": "AI Gaming Coach",
    "lp.f6.desc": "AI coach for aim drills, macro plays, mental game and more.",
    "lp.f7.title": "Zombies Squad Finder",
    "lp.f7.desc": "Find players for Easter Eggs, High Rounds, boss fights and chill runs.",
    "lp.trust": "Free Beta • No credit card required",
    "lp.preview.kicker": "Try before you sign up",
    "lp.preview.title": "Preview FicoPilot — no signup needed",
    "lp.preview.sub": "Check country trends, see live teammates, and get a game recommendation in seconds.",
    "lp.pricing.free.f1": "Find teammates worldwide",
    "lp.pricing.free.f2": "Country trends & meta builds",
    "lp.pricing.free.f3": "Basic FPS fix wizard",
    "lp.pricing.free.f4": "3 AI coach messages / day",
    "lp.pricing.premium.f1": "Unlimited AI Coach",
    "lp.pricing.premium.f2": "Priority matchmaking",
    "lp.pricing.premium.f3": "Advanced meta data",
    "lp.pricing.premium.f4": "Early access to new features",
    "lp.pricing.premium.f5": "Ad-free",
    "lp.pricing.kicker": "Membership",
    "lp.pricing.title": "Simple pricing, built to scale",
    "lp.pricing.sub": "Start free. Upgrade when you are ready to dominate.",
    "lp.pricing.free_name": "Free Pilot",
    "lp.pricing.free_cta": "Start for Free",
    "lp.pricing.premium_name": "Premium",
    "lp.pricing.premium_cta": "Try Premium",
    "lp.pricing.forever": "forever",
    "lp.pricing.per_month": "/ month",
    "lp.pricing.popular": "Most Popular",
    "lp.preview_banner": "Browsing as guest · Sign up to unlock everything",
    "lp.preview_cta": "Sign up free",

    // Login
    "login.title_prefix": "Sign in to",
    "login.subtitle": "Sign in with Google — your Pilot awaits.",
    "login.signin_h": "Sign in to ",
    "login.signin_sub": "Jump back into your squad, your builds, your coach.",
    "login.create_h": "Create your ",
    "login.create_sub": "Build your gamer identity. Pick a unique nickname next — no real names exposed.",
    "login.tab_signin": "Sign in",
    "login.tab_create": "Create account",
    "login.google": "Continue with Google",
    "login.apple": "Continue with Apple",
    "login.apple_soon": "Apple login coming soon.",
    "login.soon": "soon",
    "login.or": "or email",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.create_cta": "Create account",
    "login.signing_in": "Loading…",
    "login.enter_email": "Please enter your email",
    "login.enter_both": "Please enter email and password.",
    "login.pw_hint": "Min 8 chars · 1 letter · 1 number.",
    "login.pw_short": "Password: min 8 chars, 1 letter + 1 number.",
    "login.stay_signed_in": "Stay signed in",
    "login.welcome": "Welcome to FicoPilot",
    "login.welcome_new": "Account created. Let's go, Pilot.",
    "login.identity_note": "Your gamer nickname is chosen in the next step. Your real name stays private.",
    "login.terms": "By continuing you agree to the Terms & Privacy.",
    "login.welcome_back_h": "Welcome back,",
    "login.welcome_back_sub": "Pick up where you left off — your squad, your builds, your coach.",
    "login.forgot": "Forgot password?",

    // Forgot / reset / verify
    "fp.h": "Forgot your password?",
    "fp.sub": "We'll email you a secure reset link.",
    "fp.send": "Send reset link",
    "fp.sending": "Sending…",
    "fp.sent": "Email sent — if an account exists with this address.",
    "fp.check_inbox": "Check your inbox",
    "fp.check_inbox_sub": "If an account exists with this email, we've sent a reset link. Valid for 1 hour.",
    "fp.back": "Back to login",
    "fp.back_login": "Back to login",
    "rp.h": "Set a new password",
    "rp.sub": "Min 8 chars, 1 letter + 1 number.",
    "rp.save": "Save password",
    "rp.saving": "Saving…",
    "rp.done_h": "Password changed",
    "rp.done_sub": "You're signed in. Redirecting…",
    "rp.success": "Password changed. You're signed in.",
    "rp.bad_token": "Link is invalid or already used.",
    "rp.expired": "Link expired. Please request a new one.",
    "rp.no_token": "Reset link is missing or invalid.",
    "rp.back": "Back",
    "ve.verifying": "Verifying email…",
    "ve.verifying_sub": "Takes just a moment.",
    "ve.success": "Email verified",
    "ve.success_sub": "Your account is now fully active. Have fun, Pilot.",
    "ve.go_app": "Go to app",
    "ve.error": "Couldn't verify",
    "ve.expired": "Link expired.",
    "ve.used": "Link already used.",
    "ve.invalid": "Invalid link.",
    "ve.failed": "Verification failed.",
    "ve.no_token": "No token found.",
    "ve.resend": "Request new link",

    // Profile auth cluster
    "profile.verify_h": "Email not verified yet",
    "profile.verify_sub": "Verify your email so we can send password resets & important account notices.",
    "profile.verify_resend": "Resend",
    "profile.verify_sent": "Verification link sent. Check your inbox.",
    "profile.verify_failed": "Couldn't send email. Wait a moment and try again.",
    "profile.verified": "Email verified · account secured",
    "profile.logout": "Sign out",
    "profile.logout_all": "Sign out all devices",
    "profile.logout_all_confirm": "Sign out on every device?",
    "profile.logout_all_done": "Signed out everywhere.",
    "profile.logout_all_failed": "Logout failed.",

    // Onboarding avatar
    "onb.avatar_step": "Avatar",
    "onb.avatar_h": "Pick your avatar",
    "onb.avatar_sub": "Optional. Choose one of 12 gaming presets — change anytime later.",
    "onb.avatar_private": "Your real name / provider picture is never shown publicly.",
    "onb.avatar_skip": "Skip for now — continue",
    "onb.avatar_random": "Random Pilot",

    // Players Discovery + Matchmaking (NEW)
    "players.kicker": "MATCHMAKING",
    "players.title": "Find players",
    "players.sub": "Find pilots with the same game, country, language and skill — sorted by match score.",
    "players.your_favs": "Your favorite games",
    "players.f_game": "Game",
    "players.f_country": "Country",
    "players.f_lang": "Language",
    "players.f_skill": "Skill",
    "players.clear": "Clear filters",
    "players.searching": "Searching...",
    "players.found_one": "pilot found",
    "players.found_many": "pilots found",
    "players.sorted_by": "Sorted by match score",
    "players.invite": "Invite",
    "players.coach": "Coach",
    "players.empty_h": "No pilots found yet",
    "players.empty_sub": "Loosen your filters or invite friends — pilots show up here, sorted by match score, as soon as they complete their profile.",
    "players.empty_cta_profile": "Complete profile",
    "players.empty_cta_team": "View teammates",

    // Dashboard (NEW)
    "dash.players_card": "Find players",
    "dash.players_desc": "Match score based on game, country, language and skill — no bots, real pilots only.",
    "dash.favs_h": "Your favorite games · find players",
    "dash.all_players": "All pilots",

    // Profile extension (NEW)
    "profile.private_email_note": "Email private — never publicly visible",
    "profile.nickname_locked": "You can change your nickname during onboarding.",
    "profile.langs_active": "active",
    "profile.find_players_h": "Find players",
    "profile.find_players_sub": "Match score based on game, country, language and skill.",
    "profile.meta_h": "Player profile",
    "profile.meta_sub": "These details improve your match score in player search.",
    "profile.meta_save": "Save",
    "profile.meta_saved": "Profile details saved.",
    "profile.meta_failed": "Failed to save.",
    "profile.langs_h": "Languages",
    "profile.skill_h": "Skill level",
    "profile.exp_h": "Experience",
    "profile.favs_hint": "Click a game to find players for it. The X removes the favorite.",
    "profile.favs_empty": "No favorite games yet. Pick one below.",
    "profile.fav_find": "Find players",
    "profile.fav_remove": "Remove from favorites",
    "profile.add_more": "Add more games",
    "premium.upgrade_btn": "Upgrade to Premium",
    "premium.checkout_failed": "Checkout could not be started.",
    "checkout.verifying": "Verifying payment...",
    "checkout.verifying_sub": "Hang tight — Stripe is confirming your payment.",
    "checkout.success_h": "Welcome, Premium pilot!",
    "checkout.success_sub": "Payment confirmed. All Premium features are now unlocked.",
    "checkout.success_active": "active",
    "checkout.toast_paid": "Premium activated 🚀",
    "checkout.go_dashboard": "Go to dashboard",
    "checkout.see_profile": "View profile",
    "checkout.error_h": "We couldn't confirm your payment",
    "checkout.expired_sub": "This checkout session expired.",
    "checkout.timeout_sub": "We couldn't confirm your payment yet. If you were charged, email us at support@ficopilot.app.",
    "checkout.error_sub": "Something went wrong. Please try again or contact support.",
    "checkout.retry": "Try again",
    "cancel.title": "Payment canceled",
    "cancel.sub": "No worries — you weren't charged. Take another look at the Premium perks anytime.",
    "cancel.retry": "View Premium",
    "cancel.go_dashboard": "Back to dashboard",

    // Footer
    "footer.tagline": "The Global Gaming Assistant",
    "footer.impressum": "Impressum",
    "footer.privacy": "Datenschutz",
    "lp.footer.built": "Built for gamers worldwide",
    "lp.footer.live": "Live now",

    // Onboarding
    "onb.step_of": "Step {current} of {total}",
    "onb.country_h": "Select your country",
    "onb.country_sub": "We'll surface trends and teammates near you.",
    "onb.country_search": "Search countries...",
    "onb.lang_h": "Pick your language",
    "onb.lang_sub": "For better matchmaking and voice channels.",
    "onb.games_h": "Favorite games",
    "onb.games_sub": "Pick at least one — add as many as you play.",
    "onb.back": "Back",
    "onb.continue": "Continue",
    "onb.enter": "Enter FicoPilot",
    "onb.all_set": "You're all set, pilot.",
    "onb.save_failed": "Could not save. Try again.",

    // Dashboard
    "dash.kicker": "Dashboard",
    "dash.welcome": "Welcome back,",
    "dash.sub": "Pick a tool and get back in the fight.",
    "dash.go_premium": "Go Premium",
    "dash.online": "Online pilots",
    "dash.pilots": "Pilots",
    "dash.early_access": "Early access",
    "dash.live": "live",
    "dash.active": "active",
    "dash.saved": "saved",
    "dash.ping": "Your ping",
    "dash.countries": "Active countries",
    "dash.your_games": "Your games",
    "dash.open": "Open",

    // Pages
    "page.teammates.title": "Find Teammates",
    "page.teammates.sub": "Filter by game, country, language, platform, and play style.",
    "page.teammates.kicker": "Squad up",
    "page.teammates.online_only": "Online only",
    "page.teammates.mic_only": "Mic on",
    "page.teammates.filter.game": "All games",
    "page.teammates.filter.country": "All countries",
    "page.teammates.filter.language": "Any language",
    "page.teammates.filter.platform": "Any platform",
    "page.teammates.filter.mode_any": "Any mode",
    "page.teammates.filter.mode_ranked": "Ranked",
    "page.teammates.filter.mode_casual": "Casual",
    "page.teammates.refresh": "Refresh",
    "page.teammates.invite": "Invite to squad",
    "page.teammates.pilots_online": "pilots",
    "page.teammates.online_count": "online",
    "page.teammates.no_results": "No pilots match these filters. Try loosening them.",
    "page.teammates.loading": "Loading squads...",
    "page.teammates.invite_sent": "Invite sent to",

    "page.play.kicker": "Recommender",
    "page.play.title": "What Should I Play?",
    "page.play.sub": "Tell us the vibe, we'll line up your next game.",
    "page.play.mode": "Mode",
    "page.play.intensity": "Intensity",
    "page.play.session": "Session length",
    "page.play.platform": "Platform",
    "page.play.solo": "Solo",
    "page.play.team": "Team",
    "page.play.solo_desc": "Queue alone",
    "page.play.team_desc": "Squad with friends",
    "page.play.relaxed": "Relaxed",
    "page.play.competitive": "Competitive",
    "page.play.relaxed_desc": "Chill vibes",
    "page.play.competitive_desc": "Rank up",
    "page.play.short": "Short",
    "page.play.long": "Long",
    "page.play.short_desc": "< 30 min",
    "page.play.long_desc": "1+ hour",
    "page.play.any": "Any",
    "page.play.recommend": "Recommend games",
    "page.play.thinking": "Thinking...",
    "page.play.top_picks": "Top picks for you",

    "page.fps.kicker": "Performance",
    "page.fps.title": "FPS / Ping Fix Wizard",
    "page.fps.sub": "Answer two questions — get a ranked list of fixes in seconds.",
    "page.fps.what_platform": "What are you playing on?",
    "page.fps.what_platform_sub": "Pick your primary platform.",
    "page.fps.whats_happening": "What's happening?",
    "page.fps.whats_happening_sub": "Choose the symptom closest to yours.",
    "page.fps.pc": "PC",
    "page.fps.console": "Console",
    "page.fps.mobile": "Mobile",
    "page.fps.low_fps": "Low FPS",
    "page.fps.lag": "Stutter / Lag",
    "page.fps.ping": "High Ping",
    "page.fps.crash": "Crashes",
    "page.fps.get_fixes": "Get fixes",
    "page.fps.analyzing": "Analyzing...",
    "page.fps.fix_plan": "Your fix plan",
    "page.fps.start_over": "Start over",
    "page.fps.platform_label": "Platform:",
    "page.fps.issue_label": "Issue:",

    "page.trends.kicker": "Worldwide",
    "page.trends.title": "Country Trends",
    "page.trends.sub": "Live top games, peak hours, and ping benchmarks — by region.",
    "page.trends.online_k": "online",
    "page.trends.snapshot": "Live gaming snapshot",
    "page.trends.stat_online": "Online",
    "page.trends.stat_peak": "Peak hours",
    "page.trends.stat_ping": "Avg ping",
    "page.trends.stat_tourneys": "Tourneys / wk",
    "page.trends.top_games": "Top games",

    "page.builds.kicker": "Meta",
    "page.builds.title": "Meta Builds & Loadouts",
    "page.builds.sub": "Tier-ranked, role-specific, tested by top players this patch.",
    "page.builds.all": "All games",
    "page.builds.winrate": "Winrate",

    "page.coach.kicker": "FicoPilot AI",
    "page.coach.title": "AI Gaming Coach",
    "page.coach.sub": "Your personal esports coach. Concise, actionable, motivating.",
    "page.coach.for": "Coaching for:",
    "page.coach.placeholder": "Ask your coach...",
    "page.coach.hello": "Hey {name} — FicoPilot Coach. Short & direct. Ask away.",
    "page.coach.offline": "Coach is offline right now.",
    "page.coach.hiccup": "My circuits hiccupped — try that again in a moment.",

    "page.profile.kicker": "Your pilot",
    "page.profile.title": "Profile",
    "page.profile.sub": "Account, preferences and favorite games.",
    "page.profile.display_name": "Display name",
    "page.profile.email": "Email",
    "page.profile.country": "Country",
    "page.profile.language": "Language",
    "page.profile.games": "Games",
    "page.profile.games_saved": "saved",
    "page.profile.save": "Save changes",
    "page.profile.saved": "Profile saved",
    "page.profile.membership": "Membership",
    "page.profile.premium_on": "You're on Premium — enjoy unlimited AI coach.",
    "page.profile.premium_off": "Unlock unlimited AI coach, priority matchmaking & more.",
    "page.profile.manage_premium": "Manage Premium",
    "page.profile.favorite_games": "Favorite games",

    "page.premium.kicker_active": "Active",
    "page.premium.kicker_up": "Upgrade",
    "page.premium.title_pre": "FicoPilot",
    "page.premium.title_post": "Premium",
    "page.premium.sub": "Unlock the full toolkit — for the gamers who play to win.",
    "page.premium.hero_h": "Unlock every edge.",
    "page.premium.hero_sub": "Built for pilots who want to compete at a higher level — every day.",
    "page.premium.unlimited_coach": "Unlimited AI Coach",
    "page.premium.unlimited_coach_d": "No daily caps, priority queue.",
    "page.premium.priority_match": "Priority matchmaking",
    "page.premium.priority_match_d": "Your squad listings get boosted.",
    "page.premium.adv_meta": "Advanced meta data",
    "page.premium.adv_meta_d": "Pro-player setups + patch deltas.",
    "page.premium.early": "Early access to new features",
    "page.premium.early_d": "Try new tools before anyone else.",
    "page.premium.adfree": "Ad-free",
    "page.premium.adfree_d": "Zero ads, ever. Focus on the game.",
    "page.premium.monthly": "Monthly",
    "page.premium.annual": "Annual",
    "page.premium.save30": "Save 30%",
    "page.premium.start": "Start Premium",
    "page.premium.start_yearly": "Go yearly · save 30%",
    "page.premium.best_value": "Best value · save 30%",
    "page.premium.cancel_reassure": "Risk-free · cancel anytime · no hidden fees.",
    "page.premium.admin_badge": "Admin · Premium permanently unlocked.",
    "page.premium.active": "Active",
    "page.premium.cancel_anytime": "Cancel anytime",
    "page.premium.cancel_anytime_d": "One tap. No emails. No questions.",
    "page.premium.secure_checkout": "Secure checkout",
    "page.premium.secure_checkout_d": "Encrypted payments via Stripe.",
    "page.premium.scale": "Built to scale",
    "page.premium.scale_d": "Built to scale — fast, secure, multi-region.",
    "page.premium.welcome": "Welcome to Premium — {plan} plan unlocked (mock).",

    // LoginWall
    "wall.title": "Sign up to unlock",
    "wall.sub": "Create your free FicoPilot account to access this feature — no credit card required.",
    "wall.signup": "Sign up free",
    "wall.have_account": "Already have an account?",
    "wall.login": "Sign in",
    "wall.close": "Keep browsing",

    // Guest banners
    "guest.teammates_locked": "Sign up free to see all {count}+ pilots",
    "guest.teammates_locked_sub": "Filter by rank, send invites, and queue up — it takes 10 seconds.",
    "guest.premium_footer": "Already sold? Create your free account and start squading up.",
    "guest.sticky_cta": "Ready to level up? Create your free account",
    "guest.sticky_cta_btn": "Sign up",

    // common
    "common.signout": "Sign out",
    "common.language": "Language",

    // Onboarding — nickname
    "onb.nickname_step": "Nickname",
    "onb.nickname_h": "Welcome Pilot. Create your gamer identity.",
    "onb.nickname_sub": "Pick your unique tag — 3–20 chars, letters / numbers / _ . -. No real names exposed, ever.",
    "onb.nick_checking": "Checking availability…",
    "onb.nick_available": "✓ Available — looking good.",
    "onb.nick_taken": "Taken — try another one.",
    "onb.nick_too_short": "Too short — at least 3 characters.",
    "onb.nick_too_long": "Too long — max 20 characters.",
    "onb.nick_invalid": "Only letters, numbers, _, ., -",
    "onb.nick_hint": "Type your tag to check availability.",
    "onb.nick_try": "Try one of these",
    "onb.nick_reserved": "This name is reserved.",
    "onb.nick_blocked": "This name is not allowed.",

    // Zombies Hub
    "zh.title": "Zombies Hub",
    "zh.sub": "Maps, Easter Eggs, Bosses, High Rounds, Gobblegums, weapons, patches — all in one place.",
    "zh.ask_coach": "Ask Coach",
    "zh.squad": "Find Squad",
    "zh.ee_reward": "Reward",
    "zh.weak": "Weak spot",
    "zh.meta_note": "Meta shifts every patch. Ask the Coach for the current top-tier picks.",
    "zh.coach_hint": "Need step-by-step Easter-Egg guides or latest patch info? The AI Coach pulls live data.",
    "zh.open_coach": "Open Coach",
    "zh.glitch_warn_h": "Heads up — glitches can risk bans",
    "zh.glitch_warn_sub": "We only show glitches when you ask for them. Use at your own risk — some reset competitive leaderboards.",
    "zh.glitch_show": "Show glitches",
    "zh.glitch_hide": "Hide glitches",
    "zh.glitch_note": "Patch-dependent. Verify current status with the Coach before using.",
    "zh.tab.maps": "Maps",
    "zh.tab.easter_eggs": "Easter Eggs",
    "zh.tab.bosses": "Bosses",
    "zh.tab.high_round": "High Round",
    "zh.tab.gobblegums": "Gobblegums",
    "zh.tab.weapons": "Weapons",
    "zh.tab.patchnotes": "Patch Notes",
    "zh.tab.glitches": "Glitches",
    // Map page (BO7 dedicated)
    "zmap.back": "Back to Hub",
    "zmap.section.overview": "Overview",
    "zmap.section.ee": "Easter Egg Steps",
    "zmap.section.bosses": "Bosses & Tips",
    "zmap.section.weapons": "Wonder Weapons & Loadouts",
    "zmap.section.strategy": "High Round Strategy",
    "zmap.section.relics": "Relics & Rewards",
    "zmap.loading": "Generating live guide…",
    "zmap.refresh": "Refresh guide",
    "zmap.coach_cta": "Ask the AI Coach about this map",
    "zmap.squad_cta": "Find a Zombies squad",
    "zmap.updated": "Updated",
  },

  de: {
    // Nav
    "nav.home": "Start",
    "nav.teammates": "Mitspieler",
    "nav.play": "Was spielen?",
    "nav.coach": "KI-Coach",
    "nav.players": "Spieler",
    "nav.squads": "Squads",
    "nav.messages": "Nachrichten",
    "nav.trends": "Trends",
    "nav.builds": "Meta-Builds",
    "nav.fps": "FPS-Fix",
    "nav.premium": "Premium",
    "nav.admin": "Admin",
    "nav.profile": "Profil",
    "nav.features": "Funktionen",
    "nav.pricing": "Preise",
    "nav.signin": "Anmelden",
    "nav.signup": "Kostenlos starten",
    "nav.signout": "Abmelden",

    // Landing
    "lp.kicker": "Globaler Gaming-Assistent",
    "lp.headline_1": "Der Gaming-Assistent",
    "lp.headline_2": "für echte Spieler",
    "lp.sub": "Finde Mitspieler, entdecke Meta-Builds, verbessere FPS & Ping und finde sofort das richtige Game.",
    "lp.trust": "Kostenlose Beta • Keine Kreditkarte nötig",
    "lp.cta_primary": "Kostenlos starten",
    "lp.cta_secondary": "Trends entdecken",
    "lp.stats.pilots": "Piloten weltweit",
    "lp.stats.countries": "Aktive Länder",
    "lp.stats.games": "Erfasste Spiele",
    "lp.stats.uptime": "Uptime 7T",
    "lp.stats.early_label": "Early Access",
    "lp.founding_badge": "Gründungs-Pilot",
    "lp.founding_remaining": "nur noch {n} von 100 Plätzen",
    "lp.ig.kicker": "Partner Deals",
    "lp.ig.title": "🔥 Deals für Piloten",
    "lp.ig.sub": "Games, Guthaben und Angebote für echte Gamer.",
    "lp.ig.affiliate": "Affiliate-Partner von Instant Gaming",
    "teammates.empty_h": "Noch keine Piloten gefunden",
    "teammates.empty_sub": "Sei einer der ersten Piloten und baue die Community mit auf.",
    "teammates.empty_cta": "Profil vervollständigen",
    "sub.h": "Abo verwalten",
    "sub.active": "Aktiv",
    "sub.cancel_scheduled": "Kündigung geplant",
    "sub.renews_on": "Nächste Abbuchung am",
    "sub.ends_on": "Premium bleibt aktiv bis",
    "sub.portal_btn": "Abo verwalten",
    "sub.cancel_btn": "Premium kündigen",
    "sub.reactivate_btn": "Premium reaktivieren",
    "sub.cancel_modal_h": "Premium wirklich kündigen?",
    "sub.cancel_modal_sub": "Dein Premium-Zugang bleibt bis zum Ende der aktuellen Laufzeit aktiv.",
    "sub.cancel_modal_no": "Abbrechen",
    "sub.cancel_modal_yes": "Premium kündigen",
    "sub.cancel_ok": "Premium wird zum Ende der Laufzeit gekündigt.",
    "sub.cancel_failed": "Kündigung fehlgeschlagen.",
    "sub.reactivate_modal_h": "Premium reaktivieren?",
    "sub.reactivate_modal_sub": "Dein Premium-Abo wird wieder aktiv fortgeführt.",
    "sub.reactivate_ok": "Premium wurde reaktiviert.",
    "sub.reactivate_failed": "Reaktivierung fehlgeschlagen.",
    "sub.portal_failed": "Portal konnte nicht geöffnet werden.",
    "danger.h": "Danger Zone",
    "danger.sub": "Dauerhafte Aktionen — sei dir sicher, bevor du klickst.",
    "danger.deactivate_btn": "Account deaktivieren",
    "danger.deactivate_hint": "Profil + Daten werden deaktiviert. Login wird danach blockiert.",
    "danger.deactivate_modal_h": "Account wirklich deaktivieren?",
    "danger.deactivate_modal_sub": "Dein Profil, deine Teams und deine Daten werden deaktiviert. Dieser Vorgang kann möglicherweise nicht rückgängig gemacht werden.",
    "danger.deactivate_modal_no": "Abbrechen",
    "danger.deactivate_modal_yes": "Account deaktivieren",
    "danger.deactivate_ok": "Account deaktiviert.",
    "danger.deactivate_failed": "Deaktivierung fehlgeschlagen.",
    "lp.features.kicker": "Kernfunktionen",
    "lp.features.title": "Alles, was ein ernsthafter Pilot braucht",
    "lp.features.sub": "Kein Social-Media-Chaos. Nur fokussierte Tools, die deine nächste Session besser machen.",
    "lp.f1.title": "Mitspieler finden",
    "lp.f1.desc": "Finde Gamer weltweit nach Spiel, Land, Sprache und Plattform.",
    "lp.f2.title": "Was soll ich spielen?",
    "lp.f2.desc": "Persönliche Empfehlungen für Stimmung, Zeit und Squad-Größe.",
    "lp.f3.title": "FPS & Ping Fix",
    "lp.f3.desc": "Ein Assistent, der Lag, Ruckler und Abstürze in unter 60 Sekunden analysiert.",
    "lp.f4.title": "Länder-Trends",
    "lp.f4.desc": "Sieh, was in deiner Region angesagt ist — Live-Spielerzahlen und Spitzenzeiten.",
    "lp.f5.title": "Meta-Builds",
    "lp.f5.desc": "Nach Tier sortierte Loadouts, Sensitivity-Settings und Rollen-Strategien.",
    "lp.f6.title": "KI-Gaming-Coach",
    "lp.f6.desc": "KI-Coach für Aim-Drills, Makro-Plays, Mental Game und mehr.",
    "lp.f7.title": "Zombies Squad-Finder",
    "lp.f7.desc": "Finde Spieler für Easter Eggs, High Rounds, Bosskämpfe und entspannte Runden.",
    "lp.preview.kicker": "Erst testen, dann anmelden",
    "lp.preview.title": "FicoPilot ausprobieren — ganz ohne Anmeldung",
    "lp.preview.sub": "Check Länder-Trends, schau dir Live-Mitspieler an und hol dir in Sekunden eine Spielempfehlung.",
    "lp.pricing.free.f1": "Mitspieler weltweit finden",
    "lp.pricing.free.f2": "Länder-Trends & Meta-Builds",
    "lp.pricing.free.f3": "Basis FPS-Fix-Assistent",
    "lp.pricing.free.f4": "3 KI-Coach-Nachrichten / Tag",
    "lp.pricing.premium.f1": "Unbegrenzter KI-Coach",
    "lp.pricing.premium.f2": "Priorisierte Spielersuche",
    "lp.pricing.premium.f3": "Erweiterte Meta-Daten",
    "lp.pricing.premium.f4": "Früher Zugriff auf neue Features",
    "lp.pricing.premium.f5": "Werbefrei",
    "lp.pricing.kicker": "Mitgliedschaft",
    "lp.pricing.title": "Einfache Preise, skalierbar gebaut",
    "lp.pricing.sub": "Starte kostenlos. Upgrade, wenn du bereit bist zu dominieren.",
    "lp.pricing.free_name": "Free Pilot",
    "lp.pricing.free_cta": "Kostenlos starten",
    "lp.pricing.premium_name": "Premium",
    "lp.pricing.premium_cta": "Premium testen",
    "lp.pricing.forever": "für immer",
    "lp.pricing.per_month": "/ Monat",
    "lp.pricing.popular": "Beliebteste Wahl",
    "lp.preview_banner": "Du surfst als Gast · Registriere dich, um alles freizuschalten",
    "lp.preview_cta": "Kostenlos registrieren",

    // Login
    "login.title_prefix": "Anmelden bei",
    "login.subtitle": "Mit Google anmelden — dein Pilot wartet.",
    "login.signin_h": "Anmelden bei ",
    "login.signin_sub": "Zurück zu deinem Squad, deinen Builds, deinem Coach.",
    "login.create_h": "Erstelle deinen ",
    "login.create_sub": "Bau deine Gamer-Identität. Einen einzigartigen Nickname wählst du gleich im nächsten Schritt — kein echter Name wird gezeigt.",
    "login.tab_signin": "Einloggen",
    "login.tab_create": "Account erstellen",
    "login.google": "Weiter mit Google",
    "login.apple": "Weiter mit Apple",
    "login.apple_soon": "Apple Login kommt bald.",
    "login.soon": "bald",
    "login.or": "oder E-Mail",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.submit": "Einloggen",
    "login.create_cta": "Account erstellen",
    "login.signing_in": "Wird geladen …",
    "login.enter_email": "Bitte E-Mail eingeben",
    "login.enter_both": "Bitte E-Mail und Passwort angeben.",
    "login.pw_hint": "Mind. 8 Zeichen · 1 Buchstabe · 1 Zahl.",
    "login.pw_short": "Passwort: mind. 8 Zeichen, 1 Buchstabe + 1 Zahl.",
    "login.stay_signed_in": "Angemeldet bleiben",
    "login.welcome": "Willkommen bei FicoPilot",
    "login.welcome_new": "Account erstellt. Los geht's, Pilot.",
    "login.identity_note": "Dein Gamer-Nickname wird im nächsten Schritt gewählt. Dein echter Name bleibt privat.",
    "login.terms": "Mit der Nutzung akzeptierst du AGB & Datenschutz.",
    "login.welcome_back_h": "Willkommen zurück,",
    "login.welcome_back_sub": "Mach weiter, wo du aufgehört hast — dein Squad, deine Builds, dein Coach.",
    "login.forgot": "Passwort vergessen?",

    // Forgot / reset / verify
    "fp.h": "Passwort vergessen?",
    "fp.sub": "Wir senden dir einen sicheren Reset-Link per E-Mail.",
    "fp.send": "Reset-Link senden",
    "fp.sending": "Wird gesendet…",
    "fp.sent": "E-Mail gesendet, falls dieses Konto existiert.",
    "fp.check_inbox": "Check deine Inbox",
    "fp.check_inbox_sub": "Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Reset-Link gesendet. 1 Stunde gültig.",
    "fp.back": "Zurück zum Login",
    "fp.back_login": "Zurück zum Login",
    "rp.h": "Neues Passwort setzen",
    "rp.sub": "Mind. 8 Zeichen, 1 Buchstabe + 1 Zahl.",
    "rp.save": "Passwort speichern",
    "rp.saving": "Wird gespeichert…",
    "rp.done_h": "Passwort geändert",
    "rp.done_sub": "Alles gut. Du wirst gleich weitergeleitet.",
    "rp.success": "Passwort geändert. Du bist eingeloggt.",
    "rp.bad_token": "Link ist ungültig oder wurde bereits verwendet.",
    "rp.expired": "Link ist abgelaufen. Bitte neuen anfordern.",
    "rp.no_token": "Reset-Link ist ungültig oder abgelaufen.",
    "rp.back": "Zurück",
    "ve.verifying": "Bestätige E-Mail…",
    "ve.verifying_sub": "Das dauert nur einen Moment.",
    "ve.success": "E-Mail bestätigt",
    "ve.success_sub": "Dein Account ist jetzt vollständig aktiviert. Viel Spaß, Pilot.",
    "ve.go_app": "Zur App",
    "ve.error": "Konnte nicht bestätigen",
    "ve.expired": "Link abgelaufen.",
    "ve.used": "Link wurde bereits verwendet.",
    "ve.invalid": "Link ist ungültig.",
    "ve.failed": "Bestätigung fehlgeschlagen.",
    "ve.no_token": "Kein Token gefunden.",
    "ve.resend": "Neuen Link anfordern",

    // Profile auth cluster
    "profile.verify_h": "E-Mail noch nicht bestätigt",
    "profile.verify_sub": "Bestätige deine E-Mail, damit wir Passwort-Resets & wichtige Account-Infos senden können.",
    "profile.verify_resend": "Erneut senden",
    "profile.verify_sent": "Bestätigungs-Link wurde gesendet. Check deine Inbox.",
    "profile.verify_failed": "Konnte Mail nicht senden. Kurz warten, dann erneut.",
    "profile.verified": "E-Mail bestätigt · Account gesichert",
    "profile.logout": "Abmelden",
    "profile.logout_all": "Auf allen Geräten abmelden",
    "profile.logout_all_confirm": "Auf allen Geräten abmelden?",
    "profile.logout_all_done": "Überall abgemeldet.",
    "profile.logout_all_failed": "Abmelden fehlgeschlagen.",

    // Onboarding avatar
    "onb.avatar_step": "Avatar",
    "onb.avatar_h": "Wähle deinen Avatar",
    "onb.avatar_sub": "Optional. Wähle eines der 12 Gaming-Presets — du kannst es jederzeit ändern.",
    "onb.avatar_private": "Dein echter Name / Provider-Profilbild wird nie öffentlich gezeigt.",
    "onb.avatar_skip": "Später aussuchen — bitte weiter",
    "onb.avatar_random": "Zufälliger Pilot",

    // Players Discovery + Matchmaking (NEU)
    "players.kicker": "MATCHMAKING",
    "players.title": "Mitspieler finden",
    "players.sub": "Finde Pilots mit gleichem Spiel, Land, Sprache und Skill — sortiert nach Match-Score.",
    "players.your_favs": "Deine Lieblingsgames",
    "players.f_game": "Spiel",
    "players.f_country": "Land",
    "players.f_lang": "Sprache",
    "players.f_skill": "Skill",
    "players.clear": "Filter zurücksetzen",
    "players.searching": "Suche läuft...",
    "players.found_one": "Pilot gefunden",
    "players.found_many": "Pilots gefunden",
    "players.sorted_by": "Sortiert nach Match-Score",
    "players.invite": "Einladen",
    "players.coach": "Coach",
    "players.empty_h": "Noch keine Pilots gefunden",
    "players.empty_sub": "Lockere die Filter oder lade Freunde ein — sobald mehr Pilots ihr Profil vervollständigen, erscheinen sie hier sortiert nach Match-Score.",
    "players.empty_cta_profile": "Profil ergänzen",
    "players.empty_cta_team": "Teammates ansehen",

    // Dashboard (NEU)
    "dash.players_card": "Mitspieler finden",
    "dash.players_desc": "Match-Score basierend auf Spiel, Land, Sprache und Skill — keine Bots, echte Pilots.",
    "dash.favs_h": "Deine Lieblingsgames · Mitspieler finden",
    "dash.all_players": "Alle Pilots",

    // Profile extension (NEU)
    "profile.private_email_note": "E-Mail privat — nie öffentlich sichtbar",
    "profile.nickname_locked": "Nickname kannst du im Onboarding ändern.",
    "profile.langs_active": "aktiv",
    "profile.find_players_h": "Mitspieler finden",
    "profile.find_players_sub": "Match-Score basierend auf Spiel, Land, Sprache und Skill.",
    "profile.meta_h": "Spieler-Profil",
    "profile.meta_sub": "Diese Angaben verbessern deinen Match-Score in der Spielersuche.",
    "profile.meta_save": "Speichern",
    "profile.meta_saved": "Profil-Details gespeichert.",
    "profile.meta_failed": "Speichern fehlgeschlagen.",
    "profile.langs_h": "Sprachen",
    "profile.skill_h": "Skill-Level",
    "profile.exp_h": "Erfahrung",
    "profile.favs_hint": "Klicke ein Spiel, um direkt Mitspieler dafür zu finden. Das X entfernt den Favoriten.",
    "profile.favs_empty": "Noch keine Lieblingsgames. Wähle unten eines aus.",
    "profile.fav_find": "Mitspieler finden",
    "profile.fav_remove": "Aus Favoriten entfernen",
    "profile.add_more": "Mehr Spiele hinzufügen",
    "premium.upgrade_btn": "Auf Premium upgraden",
    "premium.checkout_failed": "Checkout konnte nicht gestartet werden.",
    "checkout.verifying": "Zahlung wird bestätigt...",
    "checkout.verifying_sub": "Wir prüfen deine Zahlung bei Stripe. Das dauert nur einen Moment.",
    "checkout.success_h": "Willkommen, Premium-Pilot!",
    "checkout.success_sub": "Deine Zahlung wurde bestätigt. Alle Premium-Features sind jetzt freigeschaltet.",
    "checkout.success_active": "aktiv",
    "checkout.toast_paid": "Premium erfolgreich aktiviert 🚀",
    "checkout.go_dashboard": "Zum Dashboard",
    "checkout.see_profile": "Profil ansehen",
    "checkout.error_h": "Zahlung konnte nicht bestätigt werden",
    "checkout.expired_sub": "Diese Checkout-Session ist abgelaufen.",
    "checkout.timeout_sub": "Wir konnten deine Zahlung nicht bestätigen. Falls dein Konto belastet wurde, schreib uns eine Mail an support@ficopilot.app.",
    "checkout.error_sub": "Etwas ist schiefgelaufen. Versuch es erneut oder kontaktiere den Support.",
    "checkout.retry": "Erneut versuchen",
    "cancel.title": "Zahlung abgebrochen",
    "cancel.sub": "Kein Problem — du wurdest nicht belastet. Schau dir die Premium-Vorteile in Ruhe an und starte später, wenn du soweit bist.",
    "cancel.retry": "Premium ansehen",
    "cancel.go_dashboard": "Zurück zum Dashboard",

    // Footer
    "footer.tagline": "Der globale Gaming-Assistent",
    "footer.impressum": "Impressum",
    "footer.privacy": "Datenschutz",
    "lp.footer.built": "Für Gamer weltweit gebaut",
    "lp.footer.live": "Live verfügbar",

    // Onboarding
    "onb.step_of": "Schritt {current} von {total}",
    "onb.country_h": "Wähle dein Land",
    "onb.country_sub": "Wir zeigen dir Trends und Mitspieler in deiner Nähe.",
    "onb.country_search": "Länder suchen...",
    "onb.lang_h": "Wähle deine Sprache",
    "onb.lang_sub": "Für besseres Matchmaking und Voice-Channels.",
    "onb.games_h": "Lieblingsspiele",
    "onb.games_sub": "Wähle mindestens eins — füge so viele hinzu, wie du spielst.",
    "onb.back": "Zurück",
    "onb.continue": "Weiter",
    "onb.enter": "FicoPilot betreten",
    "onb.all_set": "Alles bereit, Pilot.",
    "onb.save_failed": "Konnte nicht gespeichert werden. Versuch es nochmal.",

    // Dashboard
    "dash.kicker": "Dashboard",
    "dash.welcome": "Willkommen zurück,",
    "dash.sub": "Wähle ein Tool und steig wieder ein.",
    "dash.go_premium": "Premium holen",
    "dash.online": "Online-Piloten",
    "dash.pilots": "Piloten",
    "dash.early_access": "Early Access",
    "dash.live": "live",
    "dash.active": "aktiv",
    "dash.saved": "gespeichert",
    "dash.ping": "Dein Ping",
    "dash.countries": "Aktive Länder",
    "dash.your_games": "Deine Spiele",
    "dash.open": "Öffnen",

    // Pages
    "page.teammates.title": "Mitspieler finden",
    "page.teammates.sub": "Filtere nach Spiel, Land, Sprache, Plattform und Spielstil.",
    "page.teammates.kicker": "Squad bilden",
    "page.teammates.online_only": "Nur online",
    "page.teammates.mic_only": "Mit Mic",
    "page.teammates.filter.game": "Alle Spiele",
    "page.teammates.filter.country": "Alle Länder",
    "page.teammates.filter.language": "Alle Sprachen",
    "page.teammates.filter.platform": "Alle Plattformen",
    "page.teammates.filter.mode_any": "Alle Modi",
    "page.teammates.filter.mode_ranked": "Ranked",
    "page.teammates.filter.mode_casual": "Casual",
    "page.teammates.refresh": "Neu laden",
    "page.teammates.invite": "Zum Squad einladen",
    "page.teammates.pilots_online": "Piloten",
    "page.teammates.online_count": "online",
    "page.teammates.no_results": "Keine Piloten für diese Filter. Lockere sie etwas.",
    "page.teammates.loading": "Squads laden...",
    "page.teammates.invite_sent": "Einladung gesendet an",

    "page.play.kicker": "Empfehlung",
    "page.play.title": "Was soll ich spielen?",
    "page.play.sub": "Sag uns die Stimmung, wir haben dein nächstes Spiel parat.",
    "page.play.mode": "Modus",
    "page.play.intensity": "Intensität",
    "page.play.session": "Sitzungsdauer",
    "page.play.platform": "Plattform",
    "page.play.solo": "Solo",
    "page.play.team": "Team",
    "page.play.solo_desc": "Alleine spielen",
    "page.play.team_desc": "Squad mit Freunden",
    "page.play.relaxed": "Entspannt",
    "page.play.competitive": "Kompetitiv",
    "page.play.relaxed_desc": "Chillige Vibes",
    "page.play.competitive_desc": "Rang pushen",
    "page.play.short": "Kurz",
    "page.play.long": "Lang",
    "page.play.short_desc": "< 30 Min.",
    "page.play.long_desc": "1+ Stunde",
    "page.play.any": "Egal",
    "page.play.recommend": "Spiele empfehlen",
    "page.play.thinking": "Denke nach...",
    "page.play.top_picks": "Top-Auswahl für dich",

    "page.fps.kicker": "Performance",
    "page.fps.title": "FPS- / Ping-Assistent",
    "page.fps.sub": "Beantworte zwei Fragen — erhalte eine priorisierte Fix-Liste in Sekunden.",
    "page.fps.what_platform": "Worauf spielst du?",
    "page.fps.what_platform_sub": "Wähle deine Hauptplattform.",
    "page.fps.whats_happening": "Was ist los?",
    "page.fps.whats_happening_sub": "Wähle das Symptom, das deinem am nächsten kommt.",
    "page.fps.pc": "PC",
    "page.fps.console": "Konsole",
    "page.fps.mobile": "Mobil",
    "page.fps.low_fps": "Wenig FPS",
    "page.fps.lag": "Ruckler / Lag",
    "page.fps.ping": "Hoher Ping",
    "page.fps.crash": "Abstürze",
    "page.fps.get_fixes": "Fixes holen",
    "page.fps.analyzing": "Analysiere...",
    "page.fps.fix_plan": "Dein Fix-Plan",
    "page.fps.start_over": "Von vorn",
    "page.fps.platform_label": "Plattform:",
    "page.fps.issue_label": "Problem:",

    "page.trends.kicker": "Weltweit",
    "page.trends.title": "Länder-Trends",
    "page.trends.sub": "Live Top-Spiele, Spitzenzeiten und Ping-Benchmarks — nach Region.",
    "page.trends.online_k": "online",
    "page.trends.snapshot": "Live Gaming-Snapshot",
    "page.trends.stat_online": "Online",
    "page.trends.stat_peak": "Spitzenzeiten",
    "page.trends.stat_ping": "Ø Ping",
    "page.trends.stat_tourneys": "Turniere / Wo.",
    "page.trends.top_games": "Top-Spiele",

    "page.builds.kicker": "Meta",
    "page.builds.title": "Meta-Builds & Loadouts",
    "page.builds.sub": "Nach Tier sortiert, rollenspezifisch, getestet von Top-Spielern dieses Patches.",
    "page.builds.all": "Alle Spiele",
    "page.builds.winrate": "Winrate",

    "page.coach.kicker": "FicoPilot KI",
    "page.coach.title": "KI-Gaming-Coach",
    "page.coach.sub": "Dein persönlicher Esports-Coach. Kurz, umsetzbar, motivierend.",
    "page.coach.for": "Coaching für:",
    "page.coach.placeholder": "Frag deinen Coach...",
    "page.coach.hello": "Hey {name} — deine FicoPilot-Konsole. Kurz & direkt. Frag los.",
    "page.coach.offline": "Coach ist gerade offline.",
    "page.coach.hiccup": "Kleiner Kurzschluss — versuch es gleich nochmal.",

    "page.profile.kicker": "Dein Pilot",
    "page.profile.title": "Profil",
    "page.profile.sub": "Konto, Einstellungen und Lieblingsspiele.",
    "page.profile.display_name": "Anzeigename",
    "page.profile.email": "E-Mail",
    "page.profile.country": "Land",
    "page.profile.language": "Sprache",
    "page.profile.games": "Spiele",
    "page.profile.games_saved": "gespeichert",
    "page.profile.save": "Änderungen speichern",
    "page.profile.saved": "Profil gespeichert",
    "page.profile.membership": "Mitgliedschaft",
    "page.profile.premium_on": "Du bist auf Premium — unbegrenzter KI-Coach.",
    "page.profile.premium_off": "Schalte unbegrenzten KI-Coach, Priority-Matchmaking & mehr frei.",
    "page.profile.manage_premium": "Premium verwalten",
    "page.profile.favorite_games": "Lieblingsspiele",

    "page.premium.kicker_active": "Aktiv",
    "page.premium.kicker_up": "Upgrade",
    "page.premium.title_pre": "FicoPilot",
    "page.premium.title_post": "Premium",
    "page.premium.sub": "Schalte das volle Toolkit frei — für Gamer, die spielen, um zu gewinnen.",
    "page.premium.hero_h": "Schalte jeden Vorteil frei.",
    "page.premium.hero_sub": "Für Piloten, die jeden Tag auf höherem Niveau spielen wollen.",
    "page.premium.unlimited_coach": "Unbegrenzter KI-Coach",
    "page.premium.unlimited_coach_d": "Keine Tageslimits, Priority-Queue.",
    "page.premium.priority_match": "Priorisierte Spielersuche",
    "page.premium.priority_match_d": "Deine Squad-Einträge werden geboostet.",
    "page.premium.adv_meta": "Erweiterte Meta-Daten",
    "page.premium.adv_meta_d": "Pro-Player-Setups + Patch-Deltas.",
    "page.premium.early": "Früher Zugriff auf neue Features",
    "page.premium.early_d": "Teste neue Tools vor allen anderen.",
    "page.premium.adfree": "Werbefrei",
    "page.premium.adfree_d": "Keine Werbung. Null. Konzentrier dich aufs Spiel.",
    "page.premium.monthly": "Monatlich",
    "page.premium.annual": "Jährlich",
    "page.premium.save30": "30% sparen",
    "page.premium.start": "Premium starten",
    "page.premium.start_yearly": "Jährlich · 30% sparen",
    "page.premium.best_value": "Beste Wahl · 30% sparen",
    "page.premium.cancel_reassure": "Risikofrei · jederzeit kündbar · keine versteckten Kosten.",
    "page.premium.admin_badge": "Admin · Premium dauerhaft aktiv.",
    "page.premium.active": "Aktiv",
    "page.premium.cancel_anytime": "Jederzeit kündbar",
    "page.premium.cancel_anytime_d": "Ein Klick. Keine Mails. Keine Fragen.",
    "page.premium.secure_checkout": "Sicherer Checkout",
    "page.premium.secure_checkout_d": "Verschlüsselte Zahlung über Stripe.",
    "page.premium.scale": "Skalierbar gebaut",
    "page.premium.scale_d": "Skaliert mit dir — schnell, sicher, mehrsprachig.",
    "page.premium.welcome": "Willkommen bei Premium — {plan}-Plan freigeschaltet (Mock).",

    // LoginWall
    "wall.title": "Registriere dich zum Freischalten",
    "wall.sub": "Erstelle dein kostenloses FicoPilot-Konto, um diese Funktion zu nutzen — keine Kreditkarte nötig.",
    "wall.signup": "Kostenlos registrieren",
    "wall.have_account": "Schon ein Konto?",
    "wall.login": "Anmelden",
    "wall.close": "Weiter browsen",

    // Guest banners
    "guest.teammates_locked": "Registriere dich kostenlos für alle {count}+ Piloten",
    "guest.teammates_locked_sub": "Filter nach Rang, sende Einladungen und queue — in 10 Sekunden.",
    "guest.premium_footer": "Überzeugt? Erstelle dein kostenloses Konto und fang an zu squaden.",
    "guest.sticky_cta": "Bereit, aufzusteigen? Erstell dein kostenloses Konto",
    "guest.sticky_cta_btn": "Registrieren",

    // common
    "common.signout": "Abmelden",
    "common.language": "Sprache",

    // Onboarding — nickname
    "onb.nickname_step": "Nickname",
    "onb.nickname_h": "Willkommen, Pilot. Bau deine Gamer-Identität.",
    "onb.nickname_sub": "Wähle deinen einzigartigen Nickname — 3–20 Zeichen, Buchstaben / Zahlen / _ . -. Dein echter Name wird nie gezeigt.",
    "onb.nick_checking": "Verfügbarkeit wird geprüft…",
    "onb.nick_available": "✓ Verfügbar — sieht gut aus.",
    "onb.nick_taken": "Schon vergeben — nimm einen anderen.",
    "onb.nick_too_short": "Zu kurz — mindestens 3 Zeichen.",
    "onb.nick_too_long": "Zu lang — maximal 20 Zeichen.",
    "onb.nick_invalid": "Nur Buchstaben, Zahlen, _, ., -",
    "onb.nick_hint": "Tipp deinen Nickname ein, wir prüfen live.",
    "onb.nick_try": "Wie wär's mit",
    "onb.nick_reserved": "Dieser Name ist reserviert.",
    "onb.nick_blocked": "Dieser Name ist nicht erlaubt.",

    // Zombies Hub
    "zh.title": "Zombies Hub",
    "zh.sub": "Maps, Easter Eggs, Bosse, High Rounds, Gobblegums, Waffen, Patches — alles an einem Ort.",
    "zh.ask_coach": "Coach fragen",
    "zh.squad": "Squad finden",
    "zh.ee_reward": "Belohnung",
    "zh.weak": "Schwachstelle",
    "zh.meta_note": "Meta ändert sich mit jedem Patch. Frag den Coach nach den aktuellen Top-Picks.",
    "zh.coach_hint": "Schritt-für-Schritt Easter-Egg-Guides oder neueste Patch-Infos? Der KI-Coach zieht Live-Daten.",
    "zh.open_coach": "Coach öffnen",
    "zh.glitch_warn_h": "Achtung — Glitches können Bans riskieren",
    "zh.glitch_warn_sub": "Glitches zeigen wir nur auf Nachfrage. Nutzung auf eigenes Risiko — manche resetten Competitive-Leaderboards.",
    "zh.glitch_show": "Glitches anzeigen",
    "zh.glitch_hide": "Glitches ausblenden",
    "zh.glitch_note": "Patch-abhängig. Aktuellen Status vor Nutzung mit dem Coach prüfen.",
    "zh.tab.maps": "Maps",
    "zh.tab.easter_eggs": "Easter Eggs",
    "zh.tab.bosses": "Bosse",
    "zh.tab.high_round": "High Round",
    "zh.tab.gobblegums": "Gobblegums",
    "zh.tab.weapons": "Waffen",
    "zh.tab.patchnotes": "Patchnotes",
    "zh.tab.glitches": "Glitches",
    // Map-Seite (BO7-spezifisch)
    "zmap.back": "Zurück zum Hub",
    "zmap.section.overview": "Übersicht",
    "zmap.section.ee": "Easter-Egg-Schritte",
    "zmap.section.bosses": "Bosse & Tipps",
    "zmap.section.weapons": "Wunderwaffen & Loadouts",
    "zmap.section.strategy": "High-Round-Strategie",
    "zmap.section.relics": "Relikte & Belohnungen",
    "zmap.loading": "Live-Guide wird generiert…",
    "zmap.refresh": "Guide aktualisieren",
    "zmap.coach_cta": "KI-Coach zu dieser Map fragen",
    "zmap.squad_cta": "Zombies-Squad finden",
    "zmap.updated": "Aktualisiert",
  },
};

function detectBrowserLang() {
  // FicoPilot's primary UI language is German — default to DE for all
  // non-English browsers and only switch to EN when the user's browser
  // explicitly starts with "en" (or they've picked EN via the toggle).
  if (typeof window === "undefined") return "de";
  const raw = (navigator.language || navigator.userLanguage || "de").toLowerCase();
  if (raw.startsWith("en")) return "en";
  return "de";
}

function interpolate(template, params) {
  if (!params) return template;
  return Object.keys(params).reduce(
    (acc, key) => acc.replace(new RegExp(`\\{${key}\\}`, "g"), String(params[key])),
    template
  );
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState("de");

  useEffect(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && SUPPORTED.includes(stored)) {
      setLang(stored);
    } else {
      setLang(detectBrowserLang());
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLanguage = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLang(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
  }, []);

  const t = useCallback(
    (key, params) => {
      const table = dictionary[lang] || dictionary.en;
      const raw = table[key] ?? dictionary.en[key] ?? key;
      return interpolate(raw, params);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLanguage, t, supported: SUPPORTED }), [lang, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside I18nProvider");
  return ctx;
}

export function LanguageSwitcher({ compact = false, className = "" }) {
  const { lang, setLanguage, supported } = useT();
  return (
    <div
      className={`inline-flex items-center rounded-full bg-white/5 border border-white/10 p-0.5 ${className}`}
      data-testid="language-switcher"
    >
      {supported.map((code) => (
        <button
          key={code}
          onClick={() => setLanguage(code)}
          data-testid={`lang-${code}`}
          className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
            lang === code ? "bg-neon text-black" : "text-zinc-400 hover:text-white"
          }`}
          aria-pressed={lang === code}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
